import { Form, Formik, FormikHelpers } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { ContentArea } from '../../../shared/content'
import { CancelButton } from '../../../shared/form/button/CancelButton'
import { SubmitButton } from '../../../shared/form/button/SubmitButton'
import { LeaveDirtyFormPrompt } from '../../../shared/form/LeaveDirtyFormPrompt'
import { RadioGroup } from '../../../shared/form/radioGroup/RadioGroup'
import { RadioGroupRadio } from '../../../shared/form/radioGroup/RadioGroupRadio'
import {
  Section,
  SectionItemForm,
  SectionList,
  SectionTitle,
} from '../../../shared/section'
import { SectionActions } from '../../../shared/section/SectionActions'
import { translationsShared } from '../../../shared/translations/translationsShared'
import { translationsProfile } from '../translations/translations'
import { SelectionView } from '../types'

export type ProfileFormFields = {
  timeTrackingDefaultProjectCostCenterSelectionView: SelectionView
  articleDialogDefaultSelectionView: SelectionView
}

export function ProfileEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: ProfileFormFields
  onSubmit: (
    values: ProfileFormFields,
    formikHelpers: FormikHelpers<ProfileFormFields>
  ) => void
}) {
  const intl = useIntl()
  const navigate = useNavigate()

  const requiredField = intl.formatMessage(
    translationsShared.validationRequired
  )

  const schema = Yup.object().shape({
    timeTrackingDefaultProjectCostCenterSelectionView:
      Yup.string().required(requiredField),
    articleDialogDefaultSelectionView: Yup.string().required(requiredField),
  })

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => onSubmit(values, formikHelpers)}
      validationSchema={schema}
      validateOnChange={false}
    >
      {(values) => (
        <Form>
          <LeaveDirtyFormPrompt />
          <ContentArea>
            <Section>
              <SectionTitle>
                <FormattedMessage
                  id="profile.edit.section.timeTracking.title"
                  defaultMessage="Time Tracking Settings"
                />
              </SectionTitle>
              <SectionList>
                <SectionItemForm>
                  <RadioGroup
                    name="timeTrackingDefaultProjectCostCenterSelectionView"
                    label={intl.formatMessage({
                      id: 'profile.edit.timeTrackingDefaultProjectCostCenterSelectionView.label',
                      defaultMessage:
                        'Default view for project and cost center selection',
                    })}
                  >
                    <RadioGroupRadio
                      value={SelectionView.LIST}
                      label={intl.formatMessage(translationsProfile.list)}
                    />
                    <RadioGroupRadio
                      value={SelectionView.QR_CODE_SCANNER}
                      label={intl.formatMessage(
                        translationsProfile.qrCodeScanner
                      )}
                    />
                  </RadioGroup>
                </SectionItemForm>
              </SectionList>
            </Section>
            <Section>
              <SectionTitle>
                <FormattedMessage
                  id="profile.edit.section.article.title"
                  defaultMessage="Article Settings"
                />
              </SectionTitle>
              <SectionList>
                <SectionItemForm>
                  <RadioGroup
                    name="articleDialogDefaultSelectionView"
                    label={intl.formatMessage({
                      id: 'profile.edit.articleDialogDefaultSelectionView.label',
                      defaultMessage:
                        'Default view for article selection in activity',
                    })}
                  >
                    <RadioGroupRadio
                      value={SelectionView.LIST}
                      label={intl.formatMessage(translationsProfile.list)}
                    />
                    <RadioGroupRadio
                      value={SelectionView.QR_CODE_SCANNER}
                      label={intl.formatMessage(
                        translationsProfile.qrCodeScanner
                      )}
                    />
                  </RadioGroup>
                </SectionItemForm>
              </SectionList>
            </Section>
          </ContentArea>
          <SectionActions>
            <CancelButton onClick={() => navigate(-1)} />
            <SubmitButton />
          </SectionActions>
        </Form>
      )}
    </Formik>
  )
}
